import { ExternalLink, GitPullRequest } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FindingCountsInline } from '../components/ReviewTable';
import { DemoBadge, StatusBadge } from '../components/ui/Badges';
import { buttonClasses } from '../components/ui/Button';
import { EmptyState, ErrorState, PageHeader, SkeletonRows } from '../components/ui/Feedback';
import { usePullRequests } from '../hooks/queries';
import { formatRelative, repoLabel } from '../lib/format';

export function PullRequestsPage() {
  const { data, isLoading, error, refetch } = usePullRequests();

  return (
    <div>
      <PageHeader title="Pull Requests" color="blue" description="Every Pull Request you have reviewed, with its latest review." />
      <div className="card">
        {isLoading ? (
          <SkeletonRows rows={5} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<GitPullRequest className="h-6 w-6" />}
            title="No Pull Requests reviewed yet"
            description="Pull Requests appear here after their first review."
            action={
              <Link to="/app/new" className={buttonClasses('primary')}>
                Start a review
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {data.items.map((pr) => {
              const r = pr.latestReview;
              return (
                <li key={`${pr.owner}/${pr.repo}#${pr.prNumber}`} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center">
                  <GitPullRequest className="hidden h-5 w-5 shrink-0 text-faint sm:block" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <Link to={`/app/reviews/${r.id}`} className="font-medium hover:text-accent">
                      {pr.prTitle}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                      <span className="font-mono">
                        {repoLabel(pr)}#{pr.prNumber}
                      </span>
                      <span>by {pr.prAuthor}</span>
                      <span>
                        · {r.sourceBranch} → {r.targetBranch}
                      </span>
                      {r.source === 'demo' && <DemoBadge />}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <StatusBadge status={r.status} />
                    {r.status === 'completed' && <FindingCountsInline counts={r.findingCounts} total={r.findingTotal} />}
                    <span className="text-xs text-muted">
                      {pr.reviewCount} review{pr.reviewCount === 1 ? '' : 's'} · {formatRelative(r.createdAt)}
                    </span>
                    <a
                      href={pr.prUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-faint hover:text-ink"
                      aria-label="Open on GitHub"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
